// Types and tree adapters
import { Location } from "fhir/r5";
import {
  buildHierarchyTree,
  countHierarchyByLevel,
  countHierarchyLeaves,
  countHierarchyNodes,
  filterHierarchyTree,
  getHierarchyPath,
  HierarchyFilterResult,
  HierarchyNode,
  normalizeHierarchyKey,
  normalizeHierarchySearchValue,
} from "./hierarchyTree";
import {
  getOfficialIdentifierValue,
  getReferenceValue,
  normalizeReferenceValue,
} from "./fhirReferences";

export type LocationNode = HierarchyNode<Location>;
export type LocationFilterResult = HierarchyFilterResult<Location>;

// Lookup helpers
export function getLocationKey(location: Location): string {
  return (
    getOfficialIdentifierValue(location.identifier) ||
    normalizeReferenceValue(location.id) ||
    normalizeReferenceValue(location.name)
  );
}

export function getParentLocationKey(location: Location): string {
  return normalizeReferenceValue(getReferenceValue(location.partOf));
}

// Display helpers
export function getLocationDisplayName(location: Location): string {
  return location.name?.trim() || location.id || "N/A";
}

// Search helpers
export function getLocationSearchTerms(location: Location): string[] {
  return [
    location.id,
    location.name,
    ...(location.identifier?.map((identifier) => identifier.value) ?? []),
  ]
    .filter(Boolean)
    .map((value) => normalizeHierarchySearchValue(normalizeHierarchyKey(value)));
}

export function normalizeLocationSearchValue(value?: string | null): string {
  return normalizeHierarchySearchValue(value);
}

// Tree construction
export function buildLocationTree(locations: Location[]): LocationNode[] {
  return buildHierarchyTree(
    locations,
    getLocationKey,
    getParentLocationKey,
    getLocationDisplayName
  );
}

// Metrics and traversal
export function countLocationNodes(nodes: LocationNode[]): number {
  return countHierarchyNodes(nodes);
}

export function countLocationLeaves(nodes: LocationNode[]): number {
  return countHierarchyLeaves(nodes);
}

export function countLocationsByLevel(nodes: LocationNode[]): number[] {
  return countHierarchyByLevel(nodes);
}

// Path and child lookups
export function getLocationPath(
  location: Location | null | undefined,
  locations: Location[]
): Location[] {
  return getHierarchyPath(location, locations, getLocationKey, getParentLocationKey);
}

export function getChildLocations(
  locations: Location[],
  parentLocation: Location | null | undefined
): Location[] {
  if (!parentLocation) {
    return [];
  }

  const parentKey =
    getLocationKey(parentLocation) ||
    normalizeReferenceValue(parentLocation.id) ||
    normalizeReferenceValue(parentLocation.name);

  if (!parentKey) {
    return [];
  }

  return locations.filter(
    (candidate) => getParentLocationKey(candidate) === parentKey
  );
}

// Filtering
export function filterLocationTree(
  nodes: LocationNode[],
  searchTerm: string
): LocationFilterResult {
  return filterHierarchyTree(
    nodes,
    normalizeHierarchySearchValue(searchTerm),
    getLocationKey,
    getLocationDisplayName,
    getLocationSearchTerms
  );
}
